import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useQuestionAnswer } from '@/api/hooks/questionAnswerHooks';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { QuestionAnswerData, questionAnswerResolver } from '@/lib/validations/QuestionAnswerSchema';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';
import Text from '../Text';

interface AskQuestionFormProps {
  npoId: string;
  programId?: string;
  volunteerId?: string;
  amenityId?: string;
  messageOnSuccess?: string;
  titleOnSuccess?: string;
  onSuccess?: () => void;
}

const defaultValues: QuestionAnswerData = {
  question: '',
  answer: ''
};

const AskQuestionAnswerForm = ({ npoId, messageOnSuccess, titleOnSuccess, onSuccess }: AskQuestionFormProps) => {
  const { mutateAsync: createQuestionAnswer, isPending } = useQuestionAnswer();

  const { showError, showSuccess } = useEndFlowModal();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors }
  } = useForm<QuestionAnswerData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: questionAnswerResolver
  });

  const onSubmit: SubmitHandler<QuestionAnswerData> = async (data) => {
    createQuestionAnswer(
      {
        question: data.question,
        answer: data.answer,
        nonProfitOrgId: npoId
      },
      {
        onSuccess: (_) => {
          showSuccess({ title: titleOnSuccess ?? i18n.t('end_flow.success_title'), message: messageOnSuccess ?? '' });
          onSuccess && onSuccess();
        },
        onError: (error) => {
          console.error('Error creating a FAQ', error);
          showError({});
        }
      }
    );
  };

  return (
    <View style={style.formContainer}>
      <TextInputField
        name="question"
        control={control}
        label={i18n.t('question.add.question_label')}
        placeholder={i18n.t('question.add.question_placeholder')}
        multiline
      />
      <TextInputField
        name="answer"
        control={control}
        label={i18n.t('question.add.ask_label')}
        placeholder={i18n.t('question.add.ask_placeholder')}
        multiline
      />

      {errors.root && <Text style={style.errorText}>{errors?.root?.message}</Text>}
      <View style={style.buttonContainer}>
        <BrandedButton loading={isPending} style={style.btnStyle} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
          {i18n.t('mix.submit')}
        </BrandedButton>
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  formContainer: {
    flex: 1,
    flexDirection: 'column',
    gap: 28
  },
  buttonContainer: {
    flex: 1,
    justifyContent: 'flex-end'
  },
  btnStyle: {
    width: '100%'
  },
  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default AskQuestionAnswerForm;
